import React, { useCallback, useMemo } from 'react';

import { useMessage } from '@tmapy/intl';
import {
  Checkbox,
  FormItem,
  FormRow,
  Input,
  Select,
  SvgTrash,
  TertiaryBtn,
} from '@tmapy/style-guide';

import { useFormItemServerErrors } from '../../hooks';
import type { PriceListItemResponse } from '../../openapi/types/PriceListItem';
import { priceListItemUnitMessages } from '../../priceList/components/priceListMessages';
import { isDataPriceListItemType } from '../../priceList/utils';

import { isQuantityValid } from '../utils';
import type { DataOrderValue, ItemRowServerErrors, ServiceOrderItemRowValue } from '../utils';

import { ServiceOrderDataOrderSelect } from './ServiceOrderDataOrderSelect';
import {
  serviceOrderBtnMessages,
  serviceOrderFormMessages,
  serviceOrderMessages,
} from './serviceOrderMessages';

type ServiceOrderItemRowProps = {
  row: ServiceOrderItemRowValue;
  priceListItems: PriceListItemResponse[];
  serverErrors: ItemRowServerErrors | null;
  validate: boolean;
  isDisabled: boolean;
  canRemove: boolean;
  onChange(row: ServiceOrderItemRowValue): void;
  onRemove(key: number): void;
};

export const ServiceOrderItemRow: React.FC<ServiceOrderItemRowProps> = ({
  row,
  priceListItems,
  serverErrors,
  validate,
  isDisabled,
  canRemove,
  onChange,
  onRemove,
}) => {
  const formatMessage = useMessage();
  const formItemServerErrors = useFormItemServerErrors(serverErrors);

  const selectItems = useMemo(
    () => priceListItems.map((item) => ({ value: `${item.id}`, label: item.name })),
    [priceListItems],
  );

  const selected = priceListItems.find((item) => `${item.id}` === row.priceListItemId);
  const isDataItem = isDataPriceListItemType(selected?.type);
  const dataUnitLabel = isDataItem ? formatMessage(serviceOrderMessages.hectareUnit) : null;
  const unitLabel =
    dataUnitLabel ??
    (selected?.unit
      ? (formatMessage.lookup(priceListItemUnitMessages, selected.unit) ?? selected.unit)
      : null);
  const quantityLabel = formatMessage(
    isDataItem ? serviceOrderMessages.area : serviceOrderMessages.quantity,
  );

  const handleItemChange = useCallback(
    (_e: React.SyntheticEvent, eventData: { value: string }) => {
      const item = priceListItems.find(({ id }) => `${id}` === eventData.value);
      onChange({
        ...row,
        priceListItemId: eventData.value,
        // Backend navázání u jiné než datové položky odmítne.
        ...(isDataPriceListItemType(item?.type)
          ? {}
          : { isDataOrderLinked: false, dataOrder: null }),
      });
    },
    [row, priceListItems, onChange],
  );

  const handleQuantityChange = useCallback(
    (_e: React.SyntheticEvent, eventData?: { value: string }) =>
      onChange({ ...row, quantity: eventData?.value ?? '' }),
    [row, onChange],
  );

  const handleDocumentItemNameChange = useCallback(
    (_e: React.SyntheticEvent, eventData?: { value: string }) =>
      onChange({ ...row, documentItemName: eventData?.value ?? '' }),
    [row, onChange],
  );

  const handleDataOrderLinkChange = useCallback(
    (e: React.ChangeEvent) =>
      onChange({ ...row, isDataOrderLinked: (e.target as HTMLInputElement).checked }),
    [row, onChange],
  );

  const handleDataOrderChange = useCallback(
    (dataOrder: DataOrderValue | null) => onChange({ ...row, dataOrder }),
    [row, onChange],
  );

  const handleRemove = useCallback(() => onRemove(row.key), [row.key, onRemove]);

  const itemErrorMessage =
    formItemServerErrors.price_list_item?.errorMessage ||
    (validate && !row.priceListItemId
      ? formatMessage(serviceOrderFormMessages.priceListItemError)
      : null);
  const quantityErrorMessage =
    formItemServerErrors.quantity?.errorMessage ||
    (validate && !isQuantityValid(row.quantity)
      ? formatMessage(
        isDataItem ? serviceOrderFormMessages.areaError : serviceOrderFormMessages.quantityError,
      )
      : null);
  const documentItemNameErrorMessage =
    formItemServerErrors.price_list_item_name?.errorMessage ?? null;
  const dataOrderErrorMessage =
    formItemServerErrors.data_order?.errorMessage ||
    (validate && row.isDataOrderLinked && !row.dataOrder
      ? formatMessage(serviceOrderFormMessages.dataOrderError)
      : null);

  // Nenačtený ceník by u uložené vazby schoval i cestu k jejímu zrušení.
  const dataOrderLink = (isDataItem || row.isDataOrderLinked) && (
    <div className='sg-a-mt-1'>
      <Checkbox
        name={`items[${row.key}].link_data_order`}
        isChecked={row.isDataOrderLinked}
        isDisabled={isDisabled}
        onChange={handleDataOrderLinkChange}
      >
        {formatMessage(serviceOrderFormMessages.linkDataOrder)}
      </Checkbox>
    </div>
  );

  return (
    <div className='sg-a-d-f sg-a-g-1 sg-a-ai-b'>
      <div className='sg-a-f-a sg-u-vs-1'>
        <FormRow>
          <FormItem
            label={formatMessage(serviceOrderMessages.priceListItem)}
            isRequired
            errorMessage={itemErrorMessage}
          >
            <Select
              name={`items[${row.key}].price_list_item`}
              value={row.priceListItemId}
              items={selectItems}
              hasEmptyItem
              isRequired
              isDisabled={isDisabled}
              isInvalid={!!itemErrorMessage}
              onChange={handleItemChange}
            />
          </FormItem>
          {row.isDataOrderLinked ? (
            <ServiceOrderDataOrderSelect
              name={`items[${row.key}].data_order`}
              value={row.dataOrder}
              errorMessage={dataOrderErrorMessage}
              isDisabled={isDisabled}
              onChange={handleDataOrderChange}
            >
              {dataOrderLink}
            </ServiceOrderDataOrderSelect>
          ) : (
            <FormItem
              label={`${quantityLabel}${unitLabel ? ` (${unitLabel})` : ''}`}
              isRequired
              errorMessage={quantityErrorMessage}
            >
              <Input
                name={`items[${row.key}].quantity`}
                type='number'
                min={0.01}
                step={0.01}
                value={row.quantity}
                isDisabled={isDisabled}
                isInvalid={!!quantityErrorMessage}
                onChange={handleQuantityChange}
              />
              {dataOrderLink}
            </FormItem>
          )}
        </FormRow>
        <FormRow>
          <FormItem
            label={formatMessage(serviceOrderFormMessages.documentItemName)}
            errorMessage={documentItemNameErrorMessage}
          >
            <Input
              name={`items[${row.key}].price_list_item_name`}
              value={row.documentItemName}
              placeholder={selected?.name}
              isDisabled={isDisabled}
              isInvalid={!!documentItemNameErrorMessage}
              onChange={handleDocumentItemNameChange}
            />
          </FormItem>
        </FormRow>
      </div>
      <TertiaryBtn
        icon={{ element: <SvgTrash /> }}
        isDanger
        isDisabled={isDisabled || !canRemove}
        tooltip={formatMessage(serviceOrderBtnMessages.removeItem)}
        onClick={handleRemove}
      />
    </div>
  );
};
